import React, { useEffect, useRef } from 'react';
import { SxProps } from '@mui/system';
import { StacklineTextVariant } from '@stackline/ui';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { ProductImage } from 'src/components/BeaconV2Components/ProductCard';
import { BeaconProImageFallback, SnackbarCancel, SnackbarImportant } from 'src/components/SvgIcons';
import { CheckmarkSolidCircle } from 'src/components/SvgIcons/CheckmarkSolidCircle';
import colors from 'src/utils/colors';
import Hls from 'hls.js';

export const ContentCardHeader = ({
  title,
  children,
  id
}: {
  children?: React.ReactNode;
  title: string;
  id?: string;
}) => {
  return (
    <Flex justifyContent="space-between" alignItems="center" id={id}>
      <Text
        variant="h4"
        sx={{ fontSize: '16px', fontWeight: '700', textTransform: 'uppercase', height: '26px', lineHeight: '26px', letterSpacing: '0.96px' }}
      >
        {title}
      </Text>
      {children}
    </Flex>
  );
};

export const ContentUtilizationStatus = ({
  currentUtilization,
  maxUtilization,
  score,
  label
}: {
  currentUtilization: number;
  maxUtilization: number;
  score: number;
  label: 'character' | 'image' | 'video';
}) => {
  const pluralizedLabel = label + (maxUtilization > 1 ? 's' : '');
  return (
    <Flex flexDirection="row" alignItems="center" gap={8}>
      <Text variant="body3" fontWeight="500" sx={{ fontSize: '12px' }}>
        {`${currentUtilization}/${maxUtilization} ${pluralizedLabel}`}
      </Text>
      <UtilizationBadge value={score * 100} />
    </Flex>
  );
};

export const ContentCardContainer = ({ children, sx }: { children: React.ReactNode; sx?: SxProps }) => {
  return (
    <Flex borderRadius="8px" padding="32px 0" flexDirection="column" gap={16} sx={sx}>
      {children}
    </Flex>
  );
};

export const ContentDivider = () => {
  return <Flex sx={{ height: '1px', backgroundColor: colors.graysSecondary, width: '1040px' }} />;
};

const COLOR_MAP = {
  high: '#185450',
  medium: colors.brandClubYellow,
  low: '#FF534A'
};

const BACKGROUND_COLOR_MAP = {
  high: '#E3F3E6',
  medium: '#FFF2D6',
  low: '#FFEEED'
};
const UtilizationBadge = ({ value }: { value: number }) => {
  const status = value >= 70 ? 'high' : value >= 50 ? 'medium' : 'low';
  return (
    <Flex
      sx={{
        borderRadius: '24px',
        backgroundColor: BACKGROUND_COLOR_MAP[status],
        padding: '4px',
        paddingRight: '8px',
        minHeight: '32px'
      }}
      alignItems="center"
    >
      <Flex
        sx={{
          padding: '0 8px',
          borderRadius: '24px',
          backgroundColor: COLOR_MAP[status],
          minHeight: '24px'
        }}
        alignItems="center"
      >
        <Text color="white" variant="body3" fontWeight="700">
          {status}
        </Text>
      </Flex>
      <Text sx={{ marginLeft: '8px', color: COLOR_MAP[status] }} variant="body2" fontWeight="500">
        {value.toFixed(1)}%
      </Text>
    </Flex>
  );
};

export const AccuracyBadge = ({ isAccurate = false }: { isAccurate?: boolean | null }) => {
  const { color, backgroundColor, icon, text } = (() => {
    switch (isAccurate) {
      case true:
        return {
          color: '#185450',
          backgroundColor: '#E3F3E6',
          icon: <CheckmarkSolidCircle color="#185450" style={{ width: '24px' }} />,
          text: 'Accurate'
        };
      case false:
        return {
          color: '#FF534A',
          backgroundColor: '#FFEEED',
          icon: <SnackbarCancel style={{ width: '24px' }} />,
          text: 'Inaccurate'
        };
      case null:
        return {
          color: colors.brandClubYellow,
          backgroundColor: '#FFF8EA',
          icon: <SnackbarImportant style={{ width: '24px' }} />,
          text: 'Master content missing'
        };
      default:
        return {
          color: null,
          backgroundColor: null,
          icon: null,
          text: null
        };
    }
  })();

  return (
    <Flex
      sx={{
        borderRadius: '24px',
        backgroundColor,
        padding: '4px',
        paddingRight: '8px',
        minHeight: '32px'
      }}
      alignItems="center"
    >
      {icon}
      <Text sx={{ marginLeft: '4px', color }} variant="body2" fontWeight="500">
        {text}
      </Text>
    </Flex>
  );
};

export const ContentTextContainer = (props: {
  children: React.ReactNode;
  textVariant?: StacklineTextVariant;
  textSx?: SxProps;
}) => {
  return (
    <div
      style={{
        height: '100%'
      }}
      className="ContentTextContainer"
    >
      <Text variant={props.textVariant || 'body2'} sx={props.textSx}>
        {props.children ?? 'Missing required content.'}
      </Text>
    </div>
  );
};

const AccuracyImageCardContainer = ({
  imageContent,
  metricsContent
}: {
  imageContent: React.ReactNode;
  metricsContent: React.ReactNode;
}) => {
  return (
    <Flex flexDirection="column" sx={{ width: '504px' }}>
      <Flex
        sx={{
          border: `1px solid ${colors.graysSecondary}`,
          borderRadius: '8px 8px 0px 0px',
          padding: '24px',
          backgroundColor: '#FFFFFF',
          width: '504px',
          height: '248px',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {imageContent}
      </Flex>
      <Flex
        sx={{
          border: `1px solid ${colors.graysSecondary}`,
          borderTop: 'none',
          borderRadius: '0px 0px 8px 8px',
          width: '100%'
        }}
      >
        {metricsContent}
      </Flex>
    </Flex>
  );
};

export const ContentAccuracyImageCard = ({
  imageUri,
  mainDisplayTitle,
  secondaryDisplayTitle,
  metrics
}: ImageCardProps) => {
  const { resolution, ratio } = metrics;

  return (
    <AccuracyImageCardContainer
      imageContent={
        <ProductImage
          width="200px"
          height="200px"
          fallbackImageStyle={{ width: '200px', height: '200px' }}
          imageUri={imageUri}
          mainDisplayTitle={mainDisplayTitle}
          secondaryDisplayTitle={secondaryDisplayTitle}
          imageStyle={{ objectFit: 'contain', maxWidth: '200px', maxHeight: '200px' }}
        />
      }
      metricsContent={
        <MetricsContainer>
          <Flex flexDirection="row" gap={4}>
            <Text variant="body1" sx={{ fontWeight: '500', lineHeight: '24px' }}>
              Resolution:{' '}
            </Text>
            <Text variant="body1" sx={{ fontWeight: '400', lineHeight: '24px' }}>
              {resolution}
            </Text>
          </Flex>
          <Flex flexDirection="row" gap={4}>
            <Text variant="body1" sx={{ fontWeight: '500', lineHeight: '24px' }}>
              Ratio:{' '}
            </Text>
            <Text variant="body1" sx={{ fontWeight: '400', lineHeight: '24px' }}>
              {ratio}
            </Text>
          </Flex>
        </MetricsContainer>
      }
    />
  );
};

export const ContentAccuracyVideoCardContainer = ({
  video,
  metrics
}: {
  video: React.ReactNode;
  metrics: React.ReactNode;
}) => {
  return (
    <div style={{ borderRadius: '8px', border: `1px solid ${colors.graysSecondary}`, width: '504px' }}>
      <Flex sx={{ padding: '24px', minHeight: '266px' }} justifyContent="center" alignItems="center">
        {video}
      </Flex>
      <Flex
        sx={{
          backgroundColor: colors.contentGenBackground,
          padding: '16px',
          borderTop: `1px solid ${colors.graysSecondary}`,
          height: '83px',
          borderBottomRightRadius: '8px',
          borderBottomLeftRadius: '8px'
        }}
        flexDirection="column"
        justifyContent="center"
      >
        {metrics}
      </Flex>
    </div>
  );
};

export const ContentAccuracyVideoCard = ({ videoSrc, metrics }: VideoCardProps) => {
  const { resolution, length } = metrics;

  if (videoSrc === null) {
    return (
      <ContentAccuracyVideoCardContainer
        video={<BeaconProImageFallback style={{ width: '160px', height: '160px', margin: '0' }} />}
        metrics={
          <>
            <Text sx={{ fontSize: '16px', fontWeight: '500', lineHeight: '24px' }}>Missing required video</Text>
            <Flex sx={{ marginTop: '8px' }} flexDirection="column" gap={4}>
              Metric details will be displayed here once a video is uploaded.
            </Flex>
          </>
        }
      />
    );
  }

  return (
    <ContentAccuracyVideoCardContainer
      video={<HlsVideoPlayer videoSrc={videoSrc} width="395px" height="218px" videoStyle={{ minWidth: '395px' }} />}
      metrics={
        <Flex flexDirection="column" gap={4}>
          <Flex flexDirection="row" gap={4}>
            <Text variant="body1" sx={{ fontWeight: '500', lineHeight: '24px' }}>
              Resolution:{' '}
            </Text>
            <Text variant="body1" sx={{ fontWeight: '400', lineHeight: '24px' }}>
              {resolution}
            </Text>
          </Flex>
          <Flex flexDirection="row" gap={4}>
            <Text variant="body1" sx={{ fontWeight: '500', lineHeight: '24px' }}>
              Ratio:{' '}
            </Text>
            <Text variant="body1" sx={{ fontWeight: '400', lineHeight: '24px' }}>
              {calculateRatioFromResolution(resolution)}
            </Text>
          </Flex>
        </Flex>
      }
    />
  );
};

export const ContentAccuracyImagePlaceholder = () => {
  return (
    <AccuracyImageCardContainer
      imageContent={<BeaconProImageFallback style={{ width: '168px', height: '168px' }} />}
      metricsContent={
        <MetricsContainer>
          <Text sx={{ fontSize: '16px', fontWeight: '500', lineHeight: '24px' }}>Missing required image</Text>
          <Flex sx={{ marginTop: '8px' }} flexDirection="column" gap={4}>
            Master content is missing. Please upload or contact support.
          </Flex>
        </MetricsContainer>
      }
    />
  );
};

export const ContentAccuracyVideoPlaceholder = () => {
  return (
    <ContentAccuracyVideoCardContainer
      video={<BeaconProImageFallback style={{ width: '168px', height: '168px', margin: '0' }} />}
      metrics={
        <>
          <Text sx={{ fontSize: '16px', fontWeight: '500', lineHeight: '24px' }}>Missing required video</Text>
          <Flex sx={{ marginTop: '8px' }} flexDirection="column" gap={4}>
            Master content is missing. Please upload or contact support.
          </Flex>
        </>
      }
    />
  );
};

export const ContentUtilizationImageCardContainer = ({
  image,
  metrics
}: {
  image: React.ReactNode;
  metrics: React.ReactNode;
}) => {
  return (
    <Flex
      sx={{ borderRadius: '8px', border: `1px solid ${colors.graysSecondary}`, width: '475px', minHeight: '224px' }}
    >
      <Flex sx={{ padding: '24px', width: '222px' }} justifyContent="center" alignItems="center">
        {image}
      </Flex>
      <Flex
        sx={{
          borderTopRightRadius: '8px',
          borderBottomRightRadius: '8px',
          backgroundColor: colors.contentGenBackground,
          borderLeft: `1px solid ${colors.graysSecondary}`,
          width: '252px',
          padding: '24px'
        }}
        flexDirection="column"
        justifyContent="center"
      >
        {metrics}
      </Flex>
    </Flex>
  );
};

interface ImageCardProps {
  imageUri: string;
  mainDisplayTitle: string;
  secondaryDisplayTitle: string;
  metrics: {
    resolution: string;
    ratio: string;
  };
}

export const ContentUtilizationVideoCardPlaceholder = () => {
  return (
    <ContentUtilizationVideoCardContainer
      video={<BeaconProImageFallback style={{ width: '168px', height: '168px', margin: '0' }} />}
      metrics={
        <>
          <Text sx={{ fontSize: '16px', fontWeight: '500', lineHeight: '24px' }}>Missing required video</Text>
          <Flex sx={{ marginTop: '8px' }} flexDirection="column" gap={4}>
            Upload video to view metrics
          </Flex>
        </>
      }
    />
  );
};

export const ContentUtilizationVideoCardContainer = ({
  video,
  metrics
}: {
  video: React.ReactNode;
  metrics: React.ReactNode;
}) => {
  return (
    <div
      style={{
        borderRadius: '8px',
        border: `1px solid ${colors.graysSecondary}`,
        borderBottom: '0px',
        width: '512px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <Flex sx={{ height: '264px', width: '100%' }} justifyContent="center" alignItems="center">
        {video}
      </Flex>
      <Flex
        sx={{
          backgroundColor: colors.contentGenBackground,
          padding: '15px',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          border: `1px solid ${colors.graysSecondary}`,
          height: '84px',
          width: '100%'
        }}
        flexDirection="column"
        justifyContent="center"
      >
        {metrics}
      </Flex>
    </div>
  );
};

interface VideoCardProps {
  videoSrc: string;
  metrics: {
    resolution: string;
    length: string;
  };
}

export const ContentUtilizationVideoCard = ({ videoSrc, metrics }: VideoCardProps) => {
  const { resolution } = metrics;
  return (
    <ContentUtilizationVideoCardContainer
      video={<HlsVideoPlayer videoSrc={videoSrc} height="216px" width="391px" />}
      metrics={
        <>
          <Flex flexDirection="row" gap={4}>
            <Text variant="body1" fontWeight="500" lineHeight="24px">
              Resolution:{' '}
            </Text>
            <Text variant="body1" fontWeight="400" lineHeight="24px">
              {resolution}
            </Text>
          </Flex>
          <Flex flexDirection="row" gap={4}>
            <Text variant="body1" fontWeight="500" lineHeight="24px">
              Ratio:{' '}
            </Text>
            <Text variant="body1" fontWeight="400" lineHeight="24px">
              {calculateRatioFromResolution(resolution)}
            </Text>
          </Flex>
        </>
      }
    />
  );
};

const HlsVideoPlayer = ({
  videoSrc,
  width,
  height,
  videoStyle
}: {
  videoSrc: string;
  width: string;
  height: string;
  videoStyle?: React.CSSProperties;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current && Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(videoSrc || '');
      hls.attachMedia(videoRef.current || null);
    }
  }, [videoSrc]);

  return <video ref={videoRef} controls role="button" style={{ minWidth: '428px', width, height, ...videoStyle }} />;
};

// Calculate the ratio from the resolution. Eg 1920 x 1080 -> 16:9
const calculateRatioFromResolution = (resolution: string): string => {
  const match = resolution.match(/(\d+)\s*x\s*(\d+)/i);
  if (!match) return '1:1'; // fallback if parsing fails

  const width = parseInt(match[1], 10);
  const height = parseInt(match[2], 10);

  const gcd = (a: number, b: number): number => {
    return b === 0 ? a : gcd(b, a % b);
  };

  const commonDivisor = gcd(width, height);
  const ratioWidth = width / commonDivisor;
  const ratioHeight = height / commonDivisor;

  return `${ratioWidth}:${ratioHeight}`;
};

const MetricsContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex
      flexDirection="column"
      gap={4}
      sx={{
        backgroundColor: colors.contentGenBackground,
        padding: '16px',
        height: '84px',
        borderRadius: '0px 0px 8px 8px',
        width: '100%'
      }}
    >
      {children}
    </Flex>
  );
};

const UtilizationGridImageCardContainer = ({
  imageContent,
  metricsContent
}: {
  imageContent: React.ReactNode;
  metricsContent: React.ReactNode;
}) => {
  return (
    <Flex flexDirection="column" sx={{ width: '248px' }}>
      <Flex
        sx={{
          border: `1px solid ${colors.graysSecondary}`,
          borderRadius: '8px 8px 0px 0px',
          padding: '24px',
          backgroundColor: '#FFFFFF',
          width: '248px',
          height: '248px',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {imageContent}
      </Flex>
      <Flex
        sx={{
          border: `1px solid ${colors.graysSecondary}`,
          borderTop: 'none',
          borderRadius: '0px 0px 8px 8px',
          width: '100%'
        }}
      >
        {metricsContent}
      </Flex>
    </Flex>
  );
};

export const UtilizationGridImageCard = ({
  imageUri,
  mainDisplayTitle,
  secondaryDisplayTitle,
  metrics
}: ImageCardProps) => {
  const { resolution } = metrics;
  const calculatedRatio = calculateRatioFromResolution(resolution);

  return (
    <UtilizationGridImageCardContainer
      imageContent={
        <ProductImage
          width="176px"
          height="176px"
          fallbackImageStyle={{ width: '148px', height: '148px' }}
          imageUri={imageUri}
          mainDisplayTitle={mainDisplayTitle}
          secondaryDisplayTitle={secondaryDisplayTitle}
          imageStyle={{ objectFit: 'contain', maxWidth: '176px', maxHeight: '176px' }}
        />
      }
      metricsContent={
        <MetricsContainer>
          <Flex
            flexDirection="row"
            gap={4}
            sx={{
              '& .MuiTypography-root': {
                lineHeight: '24px'
              },
              alignItems: 'center',
              height: '24px'
            }}
          >
            <Text variant="body1" fontWeight="500">
              Resolution:{' '}
            </Text>
            <Text variant="body1" fontWeight="400">
              {resolution}
            </Text>
          </Flex>
          <Flex
            flexDirection="row"
            gap={4}
            sx={{
              '& .MuiTypography-root': {
                lineHeight: '24px'
              },
              alignItems: 'center',
              height: '24px'
            }}
          >
            <Text variant="body1" fontWeight="500">
              Ratio:{' '}
            </Text>
            <Text variant="body1" fontWeight="400">
              {calculatedRatio}
            </Text>
          </Flex>
        </MetricsContainer>
      }
    />
  );
};

export const UtilizationGridImageCardPlaceholder = () => {
  return (
    <UtilizationGridImageCardContainer
      imageContent={<BeaconProImageFallback style={{ width: '148px', height: '148px' }} />}
      metricsContent={
        <MetricsContainer>
          <Text variant="body1" fontWeight="500">
            Missing required image
          </Text>
          <Text variant="body1" sx={{ letterSpacing: '0.08px', fontWeight: '400' }}>
            Upload image to view metrics
          </Text>
        </MetricsContainer>
      }
    />
  );
};
